import { computed, inject, Injectable, signal } from '@angular/core';
import { Store } from '@ngrx/store';
import { Room } from '../models/entities/room';
import { ROOM_COLORS, RoomColor } from '../helpers/room-color.helper';
import { bookingsActions } from '../store/bookings';
import { BookingsScope } from '../store/bookings/bookings.state';
import { selectAllRooms, selectBookingsRoomsFilter } from '../store/reducers';

@Injectable({ providedIn: 'root' })
export class CalendarRoomsService {
  private store = inject(Store);
  private allRooms = this.store.selectSignal(selectAllRooms);
  private roomsFilters = {
    [BookingsScope.All]: this.store.selectSignal(
      selectBookingsRoomsFilter(BookingsScope.All),
    ),
    [BookingsScope.Personal]: this.store.selectSignal(
      selectBookingsRoomsFilter(BookingsScope.Personal),
    ),
  };
  private scopesWithAllRoomsHidden = signal(new Set<BookingsScope>());

  activeRooms = computed(() =>
    this.allRooms()
      .filter((room) => room.active)
      .sort((a, b) => a.position - b.position),
  );

  private roomColors = computed(() => {
    const chosenColorNames = new Set(
      this.activeRooms()
        .map((room) => room.color)
        .filter((colorName) => !!colorName),
    );
    const unusedColors = ROOM_COLORS.filter(
      (color) => !chosenColorNames.has(color.name),
    );
    const automaticPalette = unusedColors.length ? unusedColors : ROOM_COLORS;
    const roomsWithoutChosenColor = this.activeRooms().filter(
      (room) => !this.chosenColorOf(room),
    );
    return new Map(
      roomsWithoutChosenColor.map((room, index) => [
        room.id,
        automaticPalette[index % automaticPalette.length],
      ]),
    );
  });

  colorOf(room: Room): RoomColor {
    return (
      this.chosenColorOf(room) ??
      this.roomColors().get(room.id) ??
      ROOM_COLORS[0]
    );
  }

  private chosenColorOf(room: Room): RoomColor | undefined {
    return ROOM_COLORS.find((color) => color.name === room.color);
  }

  shownRooms(scope: BookingsScope): Room[] {
    if (this.scopesWithAllRoomsHidden().has(scope)) {
      return [];
    }
    const selectedRoomIds = this.roomsFilters[scope]()?.ids;
    return selectedRoomIds?.length
      ? this.activeRooms().filter((room) => selectedRoomIds.includes(room.id))
      : this.activeRooms();
  }

  isShown(scope: BookingsScope, room: Room): boolean {
    return this.shownRooms(scope).some((shownRoom) => shownRoom.id === room.id);
  }

  areAllShown(scope: BookingsScope): boolean {
    return this.shownRooms(scope).length === this.activeRooms().length;
  }

  toggle(scope: BookingsScope, room: Room, shown: boolean) {
    if (shown && this.scopesWithAllRoomsHidden().has(scope)) {
      this.setAllRoomsHidden(scope, false);
      this.setShownRoomIds(scope, [room.id]);
      return;
    }
    const otherShownRoomIds = this.shownRooms(scope)
      .map((shownRoom) => shownRoom.id)
      .filter((roomId) => roomId !== room.id);
    if (!shown && otherShownRoomIds.length === 0) {
      this.setAllRoomsHidden(scope, true);
      return;
    }
    this.setShownRoomIds(
      scope,
      shown ? [...otherShownRoomIds, room.id] : otherShownRoomIds,
    );
  }

  setAllShown(scope: BookingsScope, shown: boolean) {
    this.setAllRoomsHidden(scope, !shown);
    if (shown) {
      this.setShownRoomIds(scope, []);
    }
  }

  private setAllRoomsHidden(scope: BookingsScope, hidden: boolean) {
    this.scopesWithAllRoomsHidden.update((scopes) => {
      const updatedScopes = new Set(scopes);
      if (hidden) {
        updatedScopes.add(scope);
      } else {
        updatedScopes.delete(scope);
      }
      return updatedScopes;
    });
  }

  private setShownRoomIds(scope: BookingsScope, roomIds: number[]) {
    const allRoomsShown = roomIds.length === this.activeRooms().length;
    this.store.dispatch(
      bookingsActions.setRoomsFilter({
        filter: { ids: allRoomsShown ? [] : roomIds },
        scope,
      }),
    );
  }
}
